import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';

import { EncodeUrl } from '@pkg/classes';
import { Button } from '@pkg/components';
import ValidationInput from '@pkg/components/Input/ValidationInput/ValidationInput';
import { useGetEmojiTags, useUpdateEmoji } from '@pkg/hooks/admin/emoji/yat.ts';
import { type EidCategories, EidCategory } from '@pkg/types';
import { isValidUrl } from '@pkg/utils';

import config from '@app/config';
import { getDisplayUrl, type RedirectUrlItems } from '@app/helpers';
import { useEmojisStore } from '@app/store/zustand/useEmojisStore.ts';

import YatShareModule from '../ShareModule';

import { PagePreview } from './PagePreview';
import { BodyCopy, Subtitle, Wrapper } from './YatRedirectForm.style.ts';

const frontendUrl = config.publicUrl;

interface YatRedirectFormProps {
    closeForm: () => void;
}

const noUrlSet = { url: '', displayUrl: '', protocol: 'https' };

const YatRedirectForm = ({ closeForm }: YatRedirectFormProps) => {
    const { t } = useTranslation(['power_ups', 'words']);
    const emojiID = useEmojisStore((state) => state.currentEmoji);
    const { data: emojiTags } = useGetEmojiTags({ eid: emojiID });
    const { updateTags } = useUpdateEmoji();
    const [isNewUrlValid, setIsNewUrlValid] = useState<boolean>(false);
    const [isUpdateComplete, setIsUpdateComplete] = useState<boolean>(false);
    const [updateLoading, setUpdateLoading] = useState<boolean>(false);
    const [newRedirectUrl, setNewRedirectUrl] = useState<RedirectUrlItems>(noUrlSet);
    const [searchTerm, setSearchTerm] = useState('');

    const currentRedirectUrl = useMemo(() => {
        const category = '0x' + (+EidCategory.REDIRECT).toString(16).padStart(4, '0');
        const latestUrl = emojiTags ? [...emojiTags].filter((t) => t.tag === category).pop() : '';
        const url = latestUrl ? getDisplayUrl(latestUrl.data) : noUrlSet;

        setSearchTerm(url.displayUrl);

        return url;
    }, [emojiTags]);

    const urlUnchanged = Boolean(currentRedirectUrl?.url?.length && newRedirectUrl.url === currentRedirectUrl.url);

    const handleCloseForm = useCallback(() => {
        setIsNewUrlValid(false);
        setNewRedirectUrl(noUrlSet);
        setIsUpdateComplete(false);
        closeForm();
    }, [closeForm]);

    const decodeURLRecursively = useCallback((url, maxDepth = 3) => {
        if (url.indexOf('%') !== -1 && maxDepth > 0) {
            try {
                return decodeURLRecursively(decodeURIComponent(url), --maxDepth);
            } catch {
                return url;
            }
        }
        return url;
    }, []);

    // Handle form submit
    const handleSubmit = useCallback(() => {
        const category = '0x' + (+EidCategory.REDIRECT).toString(16).padStart(4, '0');
        const deleteTags = emojiTags?.filter((t) => t.tag === category).map((t) => t.hash);
        const redirectUrlTagCategory = '4';
        const url = new EncodeUrl(newRedirectUrl.url);

        const insert = [{ tag: redirectUrlTagCategory, data: url.encode() }] as unknown as EidCategories[];
        setUpdateLoading(true);
        updateTags({ eid: emojiID, value: { insert, delete: deleteTags } })
            .then((r) => {
                if (r.ok) {
                    setIsUpdateComplete(true);
                    return true;
                }
                return false;
            })
            .catch(() => {
                return false;
            })
            .finally(() => {
                setUpdateLoading(false);
                return true;
            });
    }, [emojiID, emojiTags, newRedirectUrl.url, updateTags]);

    const validateRedirectUrl = useCallback(
        (displayUrl: RedirectUrlItems): void => {
            if (
                displayUrl.url === '' ||
                !isValidUrl(encodeURI(displayUrl.url)) ||
                displayUrl.url === currentRedirectUrl.url
            ) {
                setIsNewUrlValid(false);
            } else {
                setIsNewUrlValid(true);
            }
        },
        [currentRedirectUrl.url],
    );

    const headline = isUpdateComplete ? t('redirect_success') : t('what_site', { frontendUrl, emojiID });
    const subHeadline = isUpdateComplete ? t('redirect_success_explainer_copy') : t('redirect_url_explainer_copy');

    const buttonMarkup =
        isUpdateComplete && !updateLoading ? (
            <Button variant="solid" onClick={handleCloseForm}>
                {t('done')}
            </Button>
        ) : (
            <Button variant="solid" disabled={!isNewUrlValid} onClick={handleSubmit} loading={updateLoading}>
                {t('link_up')}
            </Button>
        );

    function handleChange(e) {
        setSearchTerm(e.target.value);
    }

    useDebounce(
        () => {
            setIsNewUrlValid(false);
            const urlObj = getDisplayUrl(searchTerm);
            validateRedirectUrl(urlObj);
            setNewRedirectUrl(urlObj);
        },
        300,
        [searchTerm],
    );

    const shareMarkup =
        isUpdateComplete || urlUnchanged ? <YatShareModule yat={emojiID} classes="yat-redirect__share-module" /> : null;

    const inputMarkup = !isUpdateComplete ? (
        <ValidationInput
            label="https://"
            type="url"
            placeholder={t('your_website_placeholder')}
            onChange={handleChange}
            value={searchTerm}
            isValid={isNewUrlValid}
        />
    ) : null;

    return (
        <Wrapper>
            <Subtitle>{headline}</Subtitle>
            <BodyCopy>{subHeadline}</BodyCopy>

            <PagePreview redirectUrl={newRedirectUrl} />
            {inputMarkup}
            {shareMarkup}
            {buttonMarkup}
        </Wrapper>
    );
};

export default YatRedirectForm;
